import { Box, Button, HStack, Spinner, TabPanel } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { useHasTaskPermissionQuery } from "@src/__generated__/graphql";
import { TaskPermissionTypeEnum } from "@src/__generated__/urql-graphql";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import {
  EMPTY_EDITOR_CONTENT,
  EMPTY_EDITOR_LENGTH,
  TextEditor,
  type TextEditorRef,
} from "@src/components/ui-kit/TextEditor/TextEditor";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useRef } from "react";

export const DescriptionTab = observer(function DescriptionTab() {
  const { taskDetailModalStore: store, authStore, UIStore } = useStore();

  const ref = useRef<TextEditorRef>(null);
  const mentionedUserIds = useRef<string[]>([]);
  const attachmentIds = useRef<string[]>([]);

  const { loading: fetchingCanUpdateTask, data: canUpdateData } =
    useHasTaskPermissionQuery({
      skip: !store.disclosureState.isOpen,
      variables: {
        task_id: store.taskId.value!,
        type: authStore.isInternalUser
          ? TaskPermissionTypeEnum.Update
          : TaskPermissionTypeEnum.UpdateScoped,
      },
    });

  const turnEditModeOn = async () => {
    setTimeout(() => {
      if (store.descriptionState.is("write")) return;
      if (!canUpdateData?.hasTaskPermission) return;

      store.descriptionState.set("write");
      ref.current?.focus();

      store.leftModalPanelRef.current?.scrollTo({
        top: 9999,
        behavior: "smooth",
      });
      store.drawerBodyRef.current?.scrollTo({
        top: 9999,
        behavior: "smooth",
      });
    }, 50);
  };

  const onCancel = () => {
    ref.current?.setContent(store.latestDescription);
    store.descriptionState.set("read");
    ref.current?.clearDraft();
    store.handleChangeDescriptionInputValue(store.latestDescription);
    store.setDescriptionChanged(false);
  };

  const onSave = () => {
    if (!store.task.value) return;
    const taskFileIds: string[] = store.task.value.files.map(
      (file) => file.public_id,
    );
    const newFiles = attachmentIds.current.filter(
      (id) => !taskFileIds.includes(id),
    );

    store.updateDescription({
      description: {
        body: ref.current?.getContent() ?? EMPTY_EDITOR_CONTENT,
        mentioned_user_ids: mentionedUserIds.current,
      },
      addFiles: newFiles,
    });
  };

  const handleReadOnlyChecked = () => {
    if (store.needsRevision.value) {
      UIStore.toast({
        status: "warning",
        title: t`Reload task to update description.`,
      });
      setTimeout(() => {
        ref.current?.setContent(store.latestDescription);
        ref.current?.clearDraft();
      });
      return;
    }

    setTimeout(async () => {
      await store.updateDescription({
        description: {
          body: ref.current?.getContent() ?? EMPTY_EDITOR_CONTENT,
        },
      });
      UIStore.toast({
        status: "success",
        title: t`Description updated.`,
      });
    }, 100);
  };

  return (
    <TabPanel px="8">
      <LoadingOverlay isLoading={store.descriptionUpdating}>
        <Box
          w="full"
          ml="-2"
          px={store.descriptionState.is("read") ? "2" : "0"}
          py="2"
          _hover={{
            bg: store.descriptionState.is("read") ? "grey.50" : "transparent",
          }}
          boxSizing="content-box"
          transition="background ease 0.3s"
          rounded="lg"
        >
          <Box
            sx={
              store.descriptionState.is("write")
                ? {
                    border: "1px solid",
                    borderColor: "grey.100",
                    rounded: "lg",
                    p: "1",
                    ml: "2",
                  }
                : undefined
            }
            pos="relative"
            onClick={turnEditModeOn}
          >
            {fetchingCanUpdateTask && <Spinner size="sm" />}
            <TextEditor
              ref={ref}
              ml={store.descriptionState.is("write") ? "2" : "0"}
              // INFO: In case some client will want this back
              // draftKey={getTaskDetailDraftKey({
              //   taskId: store.taskId.value!,
              //   editorName: 'description',
              //   tabId: TaskTabIdEnum.Description,
              // })}
              // enableDrafts
              showToolbar
              isEditable={store.descriptionState.is("write")}
              taskId={store.taskId.value}
              initialValue={store.descriptionInputValue}
              attachments={(store.task.value?.files || []).map(
                store.createAttachmentFromFile,
              )}
              onReadOnlyChecked={
                !canUpdateData?.hasTaskPermission
                  ? handleReadOnlyChecked
                  : undefined
              }
              onChange={(
                editorValue,
                { mentioned_user_ids, attachment_ids },
              ) => {
                mentionedUserIds.current = mentioned_user_ids;
                attachmentIds.current = attachment_ids;

                if (!editorValue) return;
                const stringifiedValue = editorValue.toString();
                if (store.descriptionInputValue === stringifiedValue) {
                  store.setDescriptionChanged(false);
                } else if (stringifiedValue.length <= EMPTY_EDITOR_LENGTH) {
                  store.setDescriptionChanged(false);
                } else {
                  store.setDescriptionChanged(true);
                }

                store.handleChangeDescriptionInputValue(stringifiedValue);
              }}
              placeholder={t`Add a description...`}
              sx={{
                ...(store.descriptionState.is("write")
                  ? { minH: "100px", overflowY: "auto", maxH: "500px" }
                  : {}),
              }}
            />
          </Box>
          {store.descriptionState.is("write") && (
            <HStack justify="end" mt="4" spacing="2">
              <Button colorScheme="grey" onClick={onCancel} variant="outline">
                <Trans>Cancel</Trans>
              </Button>
              <Button
                isDisabled={store.needsRevision.value}
                isLoading={
                  store.descriptionUpdating || UIStore.isEditorUploading
                }
                onClick={onSave}
              >
                <Trans>Save</Trans>
              </Button>
            </HStack>
          )}
        </Box>
      </LoadingOverlay>
    </TabPanel>
  );
});
